import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { AddSchdmTemplateIdService } from './add-schdm-template-id.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams
} from '../../../shared/workspace-element-context';
import {
  AppkitDropdownItem,
  getAppkitDropdownSelection,
  mapToAppkitDropdownItems,
  setDropdownAriaLabel
} from '../../../shared/utils/appkit-dropdown.util';

interface TemplateOption {
  VALUE: string;
  LABEL: string;
}

@Component({
    selector: 'gtw-add-schdm-template-id',
    templateUrl: './add-schdm-template-id.component.html',
    styleUrls: ['./add-schdm-template-id.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AddSchdmTemplateIdComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  // Component state
  title: string = 'Add Schdm Template ID';
  minimise: boolean = false;
  crudLoading: boolean = false;
  isSaveClicked: boolean = false;
  showFormLoader: boolean = true;

  // Form data
  selectedTemplate: TemplateOption | null = null;
  selectedTemplateItem: AppkitDropdownItem | null = null;
  templateDropdownItems: AppkitDropdownItem[] = [];
  isExcluded: boolean = false;
  templateData: TemplateOption[] = [];

  // Initialization flags (V9 pattern)
  private isInitialized: boolean = false;
  private isLoading: boolean = false;

  private baseURLs: any = {};
  /** Full legacy-shaped object for service calls (nested globalParams, userSettingsData). */
  private globalService: any = {};
  private filterParams: any = {};
  private GENERAL_CONFIG: any = {};

  private readonly contextSubs = new Subscription();
  /** Incremented on workspace/global refresh so stale template HTTP callbacks are ignored. */
  private contextEpoch = 0;

  constructor(
    private addSchdmTemplateIdService: AddSchdmTemplateIdService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private globalServiceProvider: GlobalService,
    private workspaceService: WorkspaceService,
    private appService: AppService
  ) { }

  ngOnInit(): void {
    this.resetAndTryInit();
    this.contextSubs.add(
      this.workspaceService.setTabActive.subscribe(() => {
        this.resetAndTryInit();
      })
    );
    this.contextSubs.add(
      this.globalServiceProvider.globalParamsChanged$.subscribe(() => {
        this.resetAndTryInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.contextEpoch++;
    this.contextSubs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.workspaceService.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.globalServiceProvider, this.appService);
    const hydrated = hydratedGlobalParams(this.globalServiceProvider, this.workspaceService) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      filterParams: fp,
      workspaceFactory: this.workspaceService.workspaceFactory,
      getScenario: (ty: any) => this.globalServiceProvider.getScenario(ty),
      getEIN: () => this.globalServiceProvider.getEIN(),
      getTAXYEAR: () => this.globalServiceProvider.getTAXYEAR(),
      getChartType: () => this.globalServiceProvider.getChartType(),
      inputs: this.globalServiceProvider.inputs,
      sso_id: this.appService?.USER_SETTINGS?.user?.sso_id ?? ''
    };
    const gc = generalConfigFromGlobal(this.globalServiceProvider);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc;
  }

  private resetAndTryInit(): void {
    this.contextEpoch++;
    this.isInitialized = false;
    this.isLoading = false;
    this.applyWorkspaceContext();
    this.tryInit();
    this.cdr.markForCheck();
  }

  private resolveApiBase(): string {
    return String(
      this.GENERAL_CONFIG?.base_url ||
        this.GENERAL_CONFIG?.api ||
        this.baseURLs?.api ||
        this.baseURLs?.admin ||
        ''
    );
  }

  /**
   * Gate function to prevent duplicate initialization (V9 pattern)
   */
  private tryInit(): void {
    if (this.isInitialized || this.isLoading) {
      return;
    }

    const gp = extractObject(this.globalService?.globalParams);
    if (!this.globalService || !this.GENERAL_CONFIG || Object.keys(this.GENERAL_CONFIG).length === 0) {
      return;
    }

    if (!this.filterParams || Object.keys(this.filterParams).length === 0) {
      return;
    }

    if (!this.resolveApiBase()) {
      return;
    }

    if (!gp || Object.keys(gp).length === 0) {
      return;
    }

    this.isInitialized = true;
    this.init();
  }

  /**
   * Initialize component - load templates data
   */
  private init(): void {
    try {
      this.loadTemplatesData();
    } catch (e) {
      console.error('Error in init:', e);
      this.showFormLoader = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Load templates data from API
   */
  private loadTemplatesData(): void {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    this.showFormLoader = true;

    const baseUrl = this.resolveApiBase();
    const epoch = this.contextEpoch;

    this.addSchdmTemplateIdService.getTemplatesData(
      baseUrl,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (epoch !== this.contextEpoch) {
          this.isLoading = false;
          return;
        }
        if (response.callSuccess === '1' && response.jsonObject) {
          this.templateData = response.jsonObject;
        } else if (response.jsonObject) {
          this.templateData = response.jsonObject;
        }
        this.refreshTemplateDropdownItems();
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        if (epoch !== this.contextEpoch) {
          this.isLoading = false;
          return;
        }
        console.error('Error loading templates data:', error);
        this._snackBar.open('Error loading templates. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.showFormLoader = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Check if form is valid
   */
  isFormValid(): boolean {
    return this.selectedTemplate !== null && this.selectedTemplate !== undefined;
  }

  /**
   * Save template
   */
  save(): void {
    // Prevent double-click
    if (this.crudLoading) {
      this._snackBar.open('Please wait while we save this request', 'Close', {
        duration: 4000,
        panelClass: ['info-snackbar']
      });
      return;
    }

    if (!this.isFormValid()) {
      this._snackBar.open('Please select a template', 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    const baseUrl = this.resolveApiBase();
    const templateId = this.selectedTemplate!.VALUE;
    const isExcludedValue = this.isExcluded ? 'Y' : 'N';
    const epoch = this.contextEpoch;

    this.addSchdmTemplateIdService.saveTemplate(
      baseUrl,
      templateId,
      isExcludedValue,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (epoch !== this.contextEpoch) {
          this.isSaveClicked = false;
          this.crudLoading = false;
          return;
        }
        this.isSaveClicked = false;
        this.crudLoading = false;

        // Handle response - check for data property (from $http wrapper)
        const responseData = response.data || response;

        if (responseData.errorMessage && responseData.errorMessage !== 'null' && responseData.errorMessage !== null) {
          this._snackBar.open(responseData.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Update Success!', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          // Emit refresh to trigger grid update and close modal
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        if (epoch !== this.contextEpoch) {
          this.isSaveClicked = false;
          this.crudLoading = false;
          return;
        }
        this.isSaveClicked = false;
        this.crudLoading = false;
        console.error('Error saving template:', error);
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Cancel and close modal
   */
  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  private refreshTemplateDropdownItems(): void {
    this.templateDropdownItems = mapToAppkitDropdownItems(this.templateData, 'LABEL', 'VALUE');
    if (this.selectedTemplate) {
      this.selectedTemplateItem = this.templateDropdownItems.find(
        (item) => item.value === this.selectedTemplate?.VALUE
      ) || null;
    }
  }

  onTemplateSelect(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    this.selectedTemplateItem = selected;
    this.selectedTemplate = selected
      ? { VALUE: selected.value, LABEL: selected.label }
      : null;
  }

  onTemplateSelectItem(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) {
      this.selectedTemplateItem = selected;
    }
  }

  setDropdownAriaLabel(item: any, contextLabel: string = 'Option'): string {
    return setDropdownAriaLabel(item, contextLabel);
  }
}
